import { ICasinoSchema } from 'appRedux/models/casinoModels';

export interface ILatLng {
  lat: number;
  lng: number;
}

const EARTH_RADIUS_MILES = 3958.8;

const isValidLatLng = (lat: unknown, lng: unknown): boolean =>
  typeof lat === 'number' &&
  typeof lng === 'number' &&
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  Math.abs(lat) <= 90 &&
  Math.abs(lng) <= 180 &&
  // 0,0 is the default for an unset/blank location, never a real casino.
  !(lat === 0 && lng === 0);

/**
 * Returns the casino's coordinates, or null when the doc has no usable
 * `location`. Values are coerced with Number() because hand-edited Mongo docs
 * can store them as strings.
 */
export const getCasinoLatLng = (casino: Pick<ICasinoSchema, 'location'> | null | undefined): ILatLng | null => {
  const location = casino?.location;
  if (!location) {
    return null;
  }
  const lat = Number(location.lat);
  const lng = Number(location.lng);
  return isValidLatLng(lat, lng) ? { lat, lng } : null;
};

/** Great-circle (haversine) distance in miles. */
export const distanceInMiles = (from: ILatLng, to: ILatLng): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(to.lat - from.lat);
  const dLng = toRad(to.lng - from.lng);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.min(1, Math.sqrt(a)));
};

export const formatMiles = (miles: number): string => {
  if (miles < 0.1) {
    return '< 0.1 mi';
  }
  if (miles < 10) {
    return `${miles.toFixed(1)} mi`;
  }
  return `${Math.round(miles).toLocaleString('en-US')} mi`;
};

/**
 * Sorts casinos nearest-first from `origin`. Casinos without coordinates keep
 * their original relative order and go after every located casino.
 */
export const sortCasinosByDistance = <T extends Pick<ICasinoSchema, 'location'>>(
  casinos: T[],
  origin: ILatLng
): T[] =>
  casinos
    .map((casino, index) => {
      const coords = getCasinoLatLng(casino);
      return { casino, index, miles: coords ? distanceInMiles(origin, coords) : Infinity };
    })
    .sort((a, b) => (a.miles === b.miles ? a.index - b.index : a.miles - b.miles))
    .map(({ casino }) => casino);
